import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import {
  getPaymentPackages,
  createPaymentPackage,
  updatePaymentPackage,
  deletePaymentPackage,
} from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import ConfirmDialog from '../shared/ConfirmDialog';
import type { PaymentPackage } from '../../types';
import { Alert, Badge, Button, DataTable, Field, Input, Label, NumberInput, Switch, Textarea, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, formatCurrency } from '@racing/ui';

interface PackagesTabProps {
  eventId: number;
}

const emptyPackage: Partial<PaymentPackage> = {
  package_name: '',
  price: 0,
  description: '',
  included_classes: '',
  is_active: true,
};

const PackagesTab = ({ eventId }: PackagesTabProps) => {
  const [packages, setPackages] = useState<PaymentPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<Partial<PaymentPackage>>(emptyPackage);
  const [isEdit, setIsEdit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      setPackages(await getPaymentPackages(eventId));
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const handleSave = async () => {
    try {
      if (isEdit && editingPkg.package_id) {
        await updatePaymentPackage(eventId, editingPkg.package_id, editingPkg);
      } else {
        await createPaymentPackage(eventId, editingPkg);
      }
      setDialogOpen(false);
      loadPackages();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (pendingDeleteId === null) return;
    try {
      await deletePaymentPackage(eventId, pendingDeleteId);
      loadPackages();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    }
    setPendingDeleteId(null);
  };

  const columns = useMemo<ColumnDef<PaymentPackage, any>[]>(
    () => [
      { accessorKey: 'package_name', header: 'Package Name', meta: { width: 200 } },
      {
        accessorKey: 'price',
        header: 'Price',
        cell: ({ getValue }) => formatCurrency(getValue<number | null>()),
        meta: { width: 110, numeric: true },
      },
      { accessorKey: 'description', header: 'Description', meta: { width: 200, priority: 'md' } },
      {
        accessorKey: 'included_classes',
        header: 'Included Classes',
        meta: { width: 200, priority: 'lg' },
      },
      {
        accessorKey: 'is_active',
        header: 'Active',
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="neutral">Inactive</Badge>
          ),
        meta: { width: 100, priority: 'md' },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Delete ${row.original.package_name}`}
            className="text-danger hover:bg-danger-bg"
            onClick={(event) => {
              event.stopPropagation();
              setPendingDeleteId(row.original.package_id);
              setConfirmOpen(true);
            }}
          >
            <Trash2 />
          </Button>
        ),
        meta: { width: 60, incidental: true },
      },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => {
            setEditingPkg(emptyPackage);
            setIsEdit(false);
            setDialogOpen(true);
          }}
        >
          <Plus /> Add Package
        </Button>
      </div>

      <DataTable
        data={packages}
        columns={columns}
        getRowId={(row) => String(row.package_id)}
        loading={loading}
        onRowClick={(row) => {
          setEditingPkg({ ...row });
          setIsEdit(true);
          setDialogOpen(true);
        }}
        emptyMessage="No payment packages defined for this event"
        pageSizeOptions={[10, 25]}
        className="max-h-[26rem]"
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit package' : 'Add package'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Field label="Package Name" required>
              {(p) => (
                <Input
                  value={editingPkg.package_name || ''}
                  onChange={(e) => setEditingPkg({ ...editingPkg, package_name: e.target.value })}
                  {...p}
                />
              )}
            </Field>
            <Field label="Price" required>
              {(p) => (
                <NumberInput
                  value={editingPkg.price ?? null}
                  onValueChange={(value) => setEditingPkg({ ...editingPkg, price: value ?? 0 })}
                  nullable={false}
                  {...p}
                />
              )}
            </Field>
            <Field label="Description">
              {(p) => (
                <Textarea
                  rows={2}
                  value={editingPkg.description || ''}
                  onChange={(e) => setEditingPkg({ ...editingPkg, description: e.target.value })}
                  {...p}
                />
              )}
            </Field>
            <Field label="Included Classes" hint="Comma-separated class names">
              {(p) => (
                <Input
                  value={editingPkg.included_classes || ''}
                  onChange={(e) =>
                    setEditingPkg({ ...editingPkg, included_classes: e.target.value })
                  }
                  {...p}
                />
              )}
            </Field>
            <div className="flex items-center gap-3">
              <Switch
                id="package-active"
                checked={!!editingPkg.is_active}
                onCheckedChange={(checked) => setEditingPkg({ ...editingPkg, is_active: checked })}
              />
              <Label htmlFor="package-active">Active</Label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete package?"
        message="This removes the payment package from the event."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDeleteId(null);
        }}
      />
    </div>
  );
};

export default PackagesTab;
